import React from 'react';
import { modelClass, MUser } from '/imports/api/musers';
import { listFleets } from '/imports/api/fleets';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { JsonField } from '/imports/ui/JsonField';
import { useMethod } from '/imports/hooks';

/**
 * Per-fleet credit, with fleet ids resolved to names.
 *
 * A balance under a fleet that no longer exists is owed money, not garbage, so
 * it renders flagged rather than hidden -- deleting a fleet must never make a
 * customer's credit invisible to the operator.
 */
const CreditBreakdown = ({ credits }: { credits?: Record<string, number> }) => {
  const [fleetRes] = useMethod(listFleets, { limit: 500 }, []);
  const fleets = (fleetRes?.documents ?? []) as { _id: string; name: string }[];
  const nameOf = (scope: string) => {
    if (scope === 'none') return 'No fleet (shared pot)';
    const f = fleets.find((x) => x._id === scope);
    return f ? f.name : `${scope} (deleted fleet)`;
  };
  const entries = Object.entries(credits ?? {}).filter(([, v]) => Number(v) !== 0);
  if (!entries.length) return <div className="text-caption text-fg-3">No credit in any fleet.</div>;
  return (
    <div className="rounded-card border border-soft bg-panel-alt p-3">
      {entries.map(([scope, v]) => (
        <div key={scope} className="flex justify-between gap-4 py-0.5 text-ui">
          <span>{nameOf(scope)}</span>
          <span className="font-mono tabular-nums">{Number(v)}</span>
        </div>
      ))}
    </div>
  );
};

export const MUserPage = () => (
  <ModelFormPage
    modelClass={modelClass as any}
    permission={(user) => !!user?.isSuperUser}
    formSpec={() => [
      [{ field: 'name', label: 'Name' }],
      [{ field: 'email', label: 'Email' }],
      [{ field: 'utyp', label: 'User Type' }],
      [{ field: 'clan', label: 'Clan' }],
      [{
        field: 'credits',
        input: (_setUpdate, update, document) => (
          <CreditBreakdown credits={
            ('credits' in ((update ?? {}) as object)
              ? (update as any).credits
              : (document as any)?.credits) as Record<string, number> | undefined
          } />
        ),
      }],
      [{
        field: 'credits',
        input: (setUpdate, _update, document) => (
          <JsonField<MUser>
            field="credits"
            label="Credits by fleet (corrections)"
            tip={'Fleet id (or "none") -> tix. Edit for manual corrections; the breakdown above shows the same data with names.'}
            initial={(document as any)?.credits}
            validate={(v) => (v !== null && typeof v === 'object' && !Array.isArray(v) ? null : 'Must be a JSON object of fleetId -> number')}
            emptyValue={null}
            setUpdate={setUpdate as any}
            className="h-24! font-mono text-caption"
          />
        ),
      }],
    ]}
  />
);
